import { makeAutoObservable } from "mobx";
import {
  BatchInvitation,
  CreateUserInvitation,
  Invitation,
  InvitationType,
} from "@/types/Invitation";
import UserStore from "./UserStore";
import { navigateToPortfolio } from "@/utils/navigation";
import { ApiService } from "@/api";
import { TMessage } from "@/i18n/tt";
import AlertMessageStore from "./AlertMessageStore";
import { History } from "history";

export default class InvitationStore {
  readonly userStore: UserStore;
  readonly invitationId: string;
  readonly invitationToken: string;
  readonly createUserInvitation: CreateUserInvitation;
  readonly messageStore: AlertMessageStore;
  readonly history: History;

  state: "loading" | "load-error" | "ready" | "accept-loading" | "accept-error" = "loading";
  invitation: Invitation | null = null;

  constructor(
    userStore: UserStore,
    invitationId: string,
    invitationToken: string,
    private apiService: ApiService,
    messageStore: AlertMessageStore,
    history: History,
  ) {
    this.userStore = userStore;
    this.invitationId = invitationId;
    this.invitationToken = invitationToken;
    this.createUserInvitation = { type: "user", invitationId, invitationToken };
    this.messageStore = messageStore;
    this.history = history;
    userStore.setAuthenticationUrlParameters(invitationId, invitationToken);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *fetchInvitation() {
    try {
      this.invitation = yield this.apiService.fetchInvitation(
        this.invitationId,
        this.invitationToken,
      );
      this.state = "ready";
    } catch (e) {
      this.state = "load-error";
    }
  }

  *acceptInvitation() {
    if (this.state !== "ready") {
      return;
    }
    try {
      this.state = "accept-loading";
      yield this.apiService.acceptInvitation(this.invitationId, this.invitationToken);
      this.messageStore.showSuccess({ key: "invitation.accept-success" });
      this.userStore.refreshUserInformation();
      navigateToPortfolio(this.history);
    } catch (e) {
      this.state = "accept-error";
    }
  }

  get isLoading() {
    return this.state === "loading";
  }

  get isLoadError() {
    return this.state === "load-error";
  }

  get isLoggedIn() {
    return Boolean(this.userStore.user);
  }

  get isAcceptLoading() {
    return this.state === "accept-loading";
  }

  get isAcceptError() {
    return this.state === "accept-error";
  }

  get isCompanyInvitation() {
    return this.invitation?.invitationType === InvitationType.COMPANY;
  }
}

export class BatchInvitationStore {
  readonly userStore: UserStore;
  readonly batchInvitationId: string;
  readonly token: string;
  readonly createUserInvitation: CreateUserInvitation;
  readonly history: History;

  state: "loading" | "load-error" | "ready" | "accept-loading" | "accept-error" = "loading";
  invitation: BatchInvitation | null = null;

  constructor(
    userStore: UserStore,
    batchInvitationId: string,
    token: string,
    private apiService: ApiService,
    history: History,
  ) {
    this.userStore = userStore;
    this.history = history;
    this.batchInvitationId = batchInvitationId;
    this.token = token;
    this.createUserInvitation = { type: "batch", batchInvitationId, invitationToken: token };
    userStore.setAuthenticationUrlParameters(batchInvitationId, token, "batchInvitationId");
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *fetchBatchInvitation() {
    try {
      this.invitation = yield this.apiService.fetchBatchInvitation(
        this.batchInvitationId,
        this.token,
      );
      this.state = "ready";
    } catch (e) {
      this.state = "load-error";
    }
  }

  *acceptInvitation() {
    if (this.state !== "ready") {
      return;
    }
    try {
      this.state = "accept-loading";
      yield this.apiService.acceptBatchInvitation(this.batchInvitationId, this.token);
      this.userStore.refreshUserInformation();
      navigateToPortfolio(this.history);
    } catch (e) {
      this.state = "accept-error";
    }
  }

  get isLoading() {
    return this.state === "loading";
  }

  get isLoadError() {
    return this.state === "load-error";
  }

  get isLoggedIn() {
    return Boolean(this.userStore.user);
  }

  get isAcceptLoading() {
    return this.state === "accept-loading";
  }

  get isAcceptError() {
    return this.state === "accept-error";
  }
}

export function invitationTypeLabel(invitation: Invitation): TMessage {
  switch (invitation.invitationType) {
    case InvitationType.USER:
      return invitation.customRoleName
        ? {
            key: "invitation.invitation-type-label.custom-role",
            params: {
              invitedByName: invitation.invitedByName,
              invitedByEmail: invitation.invitedByEmail,
              roleName: invitation.customRoleName,
              companyName: invitation.companyName,
            },
          }
        : {
            key: "invitation.invitation-type-label.administrator",
            params: {
              invitedByName: invitation.invitedByName,
              invitedByEmail: invitation.invitedByEmail,
              companyName: invitation.companyName,
            },
          };
    case InvitationType.SHAREHOLDER_REPRESENTATIVE:
      return {
        key: "invitation.invitation-type-label.additional-representative",
        params: {
          invitedByName: invitation.invitedByName,
          invitedByEmail: invitation.invitedByEmail,
          shareholderName: invitation.shareholderName,
          companyName: invitation.companyName,
        },
      };
    case InvitationType.SHAREHOLDER:
      return invitation.isCompanyShareholder
        ? {
            key: "invitation.invitation-type-label.company-shareholder",
            params: {
              invitedByName: invitation.invitedByName,
              invitedByEmail: invitation.invitedByEmail,
              shareholderName: invitation.shareholderName,
              companyName: invitation.companyName,
            },
          }
        : {
            key: "invitation.invitation-type-label.person-shareholder",
            params: {
              invitedByName: invitation.invitedByName,
              invitedByEmail: invitation.invitedByEmail,
              shareholderName: invitation.shareholderName,
              companyName: invitation.companyName,
            },
          };
    case InvitationType.ACCOUNTANT:
      return {
        key: "invitation.invitation-type-label.accountant",
        params: {
          invitedByName: invitation.invitedByName,
          invitedByEmail: invitation.invitedByEmail,
          companyName: invitation.companyName,
        },
      };
    case InvitationType.BOARD_MEMBER:
      return {
        key: "invitation.invitation-type-label.board-member",
        params: {
          invitedByName: invitation.invitedByName,
          invitedByEmail: invitation.invitedByEmail,
          companyName: invitation.companyName,
        },
      };
    default:
      return {
        key: "invitation.invitation-type-label.administrator",
        params: {
          invitedByName: invitation.invitedByName,
          invitedByEmail: invitation.invitedByEmail,
          companyName: invitation.companyName,
        },
      };
  }
}
